// Pure helpers для Журнала визитов (Mini-sprint 6-visits Stage 4 §1).
// Source of truth: docs/specs/sprint-6-visits/stage-1-contracts.md §9.1.

import type { Visit } from '@/api/types';

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
] as const;

const MONTHS_NOMINATIVE = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
] as const;

/** YYYY-MM-DD → локальная дата (без UTC-сдвига). Важно для дат без времени. */
function parseLocalDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * "2026-03-15" → "15 марта" (если тот же год, что now)
 * "2025-03-15" + now=2026 → "15 марта 2025"
 */
export function formatVisitDate(isoDate: string, now: Date = new Date()): string {
  const d = parseLocalDate(isoDate);
  if (!d) return '—';
  const day = d.getDate();
  const month = MONTHS_GENITIVE[d.getMonth()];
  const year = d.getFullYear();
  if (year === now.getFullYear()) return `${day} ${month}`;
  return `${day} ${month} ${year}`;
}

/**
 * Полная дата всегда с годом: "2026-03-15" → "15 марта 2026".
 * Используется в детали визита (s13b) — там год показываем всегда.
 */
export function formatVisitDateFull(isoDate: string): string {
  const d = parseLocalDate(isoDate);
  if (!d) return '—';
  return `${d.getDate()} ${MONTHS_GENITIVE[d.getMonth()]} ${d.getFullYear()}`;
}

function ruPlural(n: number, forms: readonly [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}

const pluralDay = (n: number) => ruPlural(n, ['день', 'дня', 'дней']);
const pluralWeek = (n: number) => ruPlural(n, ['неделя', 'недели', 'недель']);
const pluralMonth = (n: number) => ruPlural(n, ['месяц', 'месяца', 'месяцев']);
const pluralYear = (n: number) => ruPlural(n, ['год', 'года', 'лет']);

/**
 * "сегодня" / "вчера" / "завтра" / "2 дня назад" / "3 недели назад" /
 * "год назад" / "через 2 дня". Locale ru.
 */
export function formatRelativeDate(isoDate: string, now: Date = new Date()): string {
  const d = parseLocalDate(isoDate);
  if (!d) return '';
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'сегодня';
  if (diffDays === -1) return 'вчера';
  if (diffDays === 1) return 'завтра';

  const past = diffDays < 0;
  const abs = Math.abs(diffDays);

  if (abs < 7) return past ? `${abs} ${pluralDay(abs)} назад` : `через ${abs} ${pluralDay(abs)}`;
  if (abs < 30) {
    const w = Math.round(abs / 7);
    return past ? `${w} ${pluralWeek(w)} назад` : `через ${w} ${pluralWeek(w)}`;
  }
  if (abs < 365) {
    const m = Math.round(abs / 30);
    return past ? `${m} ${pluralMonth(m)} назад` : `через ${m} ${pluralMonth(m)}`;
  }
  const y = Math.round(abs / 365);
  return past ? `${y} ${pluralYear(y)} назад` : `через ${y} ${pluralYear(y)}`;
}

export type VisitMonthGroup = { monthLabel: string; visits: Visit[] };

/**
 * Группирует visits по году+месяцу (visit_date). Предполагает, что входной
 * список УЖЕ отсортирован сервером DESC by visit_date. Visits с невалидной
 * датой пропускаются.
 */
export function groupVisitsByMonth(visits: Visit[]): VisitMonthGroup[] {
  const groups: VisitMonthGroup[] = [];
  let currentKey = '';
  let currentGroup: VisitMonthGroup | null = null;

  for (const v of visits) {
    const d = parseLocalDate(v.visit_date);
    if (!d) continue;
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key !== currentKey || currentGroup === null) {
      const label = `${MONTHS_NOMINATIVE[d.getMonth()]} ${d.getFullYear()}`;
      currentGroup = { monthLabel: label, visits: [] };
      groups.push(currentGroup);
      currentKey = key;
    }
    currentGroup.visits.push(v);
  }
  return groups;
}

/**
 * 500 → "500 Б", 491520 → "480 КБ", 1048576 → "1.0 МБ".
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} КБ`;
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} МБ`;
}

/** Plural для счётчика записей: "N запись/записи/записей". */
export function pluralVisits(n: number): string {
  return ruPlural(n, ['запись', 'записи', 'записей']);
}
